import { useState } from 'react'

import { CITATION_BRAND_COLORS, faviconUrl } from '@/common/app/modules/media/citation'
import {
  CitationBrand,
  CitationDomainChartRow,
  CompetitorOnlyDomain
} from '@/common/model/media/citation'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import HorizontalBarChart from '@/components/view/atoms/chart/HorizontalBarChart'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Line from '@/components/view/layouts/area/Line'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'

import GeoChartCard from '@/components/container/modules/mediaGeo/common/GeoChartCard'
import GeoSection from '@/components/container/modules/mediaGeo/common/GeoSection'

import CitationBrandLegend from './CitationBrandLegend'

interface Props {
  brands: CitationBrand[]
  domainChart: CitationDomainChartRow[]
  compOnlyDomains: CompetitorOnlyDomain[]
  competitorName?: string
}

// 初期表示するドメイン数
const VISIBLE_DOMAINS = 10

// ファビコンは取得できないドメインがあるため、失敗時は隠す
const Favicon = ({ domain }: { domain: string }) => (
  <img
    src={faviconUrl(domain)}
    width={14}
    height={14}
    alt=''
    onError={(e) => {
      ;(e.currentTarget as HTMLImageElement).style.display = 'none'
    }}
  />
)

// 言及元ドメイン（ドメイン別の言及件数比較と内訳テーブル）
const CitationDomainChart = ({ brands, domainChart, compOnlyDomains, competitorName }: Props) => {
  const [showMore, setShowMore] = useState(false)

  const ownBrand = brands[0]
  // 自社の言及件数が多い順に並べ、その並び順を「自社順位」として示す
  const sorted = [...domainChart].sort(
    (a, b) => Number(b[ownBrand?.name] || 0) - Number(a[ownBrand?.name] || 0)
  )
  const rows = showMore ? sorted : sorted.slice(0, VISIBLE_DOMAINS)

  return (
    <GeoSection
      title='言及元ドメイン'
      subText='ブランドが言及されている主なドメイン'
      subNode={<CitationBrandLegend brands={brands} />}
      noPadding
    >
      <>
        <div className='grid grid-cols-1 lg:grid-cols-2'>
          <div className='p-4'>
            <GeoChartCard title='ドメイン別 言及件数比較' height={Math.max(320, rows.length * 36)}>
              <HorizontalBarChart
                labels={rows.map((row) => row.domain)}
                items={brands.map((brand, i) => ({
                  title: brand.name,
                  values: rows.map((row) => Number(row[brand.name] || 0)),
                  color: CITATION_BRAND_COLORS[i % CITATION_BRAND_COLORS.length]
                }))}
              />
            </GeoChartCard>
          </div>
          <div className='flex flex-col gap-3'>
            <table className='border-secondary w-full border-collapse border-l'>
              <thead>
                <TableRow head>
                  <TableHeadItem type='thin' width={64}>
                    <Text size={12} color={TextColor.SECONDARY}>
                      自社順位
                    </Text>
                  </TableHeadItem>
                  <TableHeadItem type='thin'>
                    <Text size={12} color={TextColor.SECONDARY}>
                      ドメイン
                    </Text>
                  </TableHeadItem>
                  {brands.map((brand, i) => (
                    <TableHeadItem key={i} type='thin' width={88}>
                      <Text size={12} color={TextColor.SECONDARY} truncate>
                        {brand.name}
                      </Text>
                    </TableHeadItem>
                  ))}
                </TableRow>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <TableRow key={i}>
                    <TableRowItem width={64} align='middle'>
                      <Text size={14}>{i + 1}</Text>
                    </TableRowItem>
                    <TableRowItem align='middle'>
                      <ItemAlign gapX={4} align='center' nowrap>
                        <Favicon domain={row.domain} />
                        <Text size={14} truncate>
                          {row.domain}
                        </Text>
                      </ItemAlign>
                    </TableRowItem>
                    {brands.map((brand, bi) => (
                      <TableRowItem key={bi} width={88} align='middle'>
                        <Text size={14}>{Number(row[brand.name] || 0)}</Text>
                      </TableRowItem>
                    ))}
                  </TableRow>
                ))}
              </tbody>
            </table>
            {sorted.length > VISIBLE_DOMAINS && (
              <div className='mb-2 flex justify-center'>
                <Button color={ButtonColor.SECONDARY} onClick={() => setShowMore((v) => !v)}>
                  {showMore ? '少なく表示' : 'すべて表示'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </>
      {/* 自社にはない言及元（競合のみが言及されているドメイン） */}
      {compOnlyDomains.length > 0 && (
        <>
          <Line />
          <AreaContent>
            <div className='flex flex-col gap-1'>
              <Text size={14} weight='bold'>
                自社にはない{competitorName ? `${competitorName}の` : ''}
                言及元ドメイン（競合のみ・上位
                {Math.min(compOnlyDomains.length, 20)}件）
              </Text>
              <Text size={12} color={TextColor.SECONDARY} lineHeight={150}>
                {compOnlyDomains
                  .slice(0, 20)
                  .map((d) => d.domain)
                  .join(' / ')}
              </Text>
            </div>
          </AreaContent>
        </>
      )}
    </GeoSection>
  )
}

export default CitationDomainChart
